/**
 * EmptyState — v2 empty / error card.
 *
 * A FlowCard with a centered glyph, Fraunces title, muted body and an optional
 * action. Replaces the legacy `empty-state card` markup on the train screens.
 */
import type { ReactNode } from 'react';

import { FlowCard } from './FlowCard.js';
import { Icon, type IconName } from './Icon.js';

interface EmptyStateProps {
  icon?: IconName;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  /** Set for error states — tints the glyph ring off-track. */
  tone?: 'neutral' | 'error';
  /** Forwarded to the FlowCard (e.g. role="alert" on errors). */
  role?: string;
}

export function EmptyState({ icon, title, body, action, tone = 'neutral', role }: EmptyStateProps) {
  const ink = tone === 'error' ? 'var(--off-track)' : 'var(--ink-3)';
  return (
    <FlowCard padding="lg" style={{ textAlign: 'center' }}>
      <div {...(role ? { role } : {})}>
        {icon && (
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 44,
              height: 44,
              margin: '0 auto 12px',
              borderRadius: 'var(--r-full)',
              background: 'var(--paper-2)',
              border: '1px solid var(--edge)',
              color: ink,
            }}
          >
            <Icon name={icon} size={20} />
          </div>
        )}
        <h3
          style={{
            margin: 0,
            fontFamily: 'var(--font-display)',
            fontSize: 17,
            fontWeight: 700,
            color: 'var(--ink-1)',
          }}
        >
          {title}
        </h3>
        {body && (
          <p
            style={{
              margin: '8px auto 0',
              maxWidth: 280,
              fontSize: 'var(--text-body-sm-size)',
              lineHeight: 'var(--text-body-sm-line)',
              color: 'var(--ink-2)',
            }}
          >
            {body}
          </p>
        )}
        {action && (
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>{action}</div>
        )}
      </div>
    </FlowCard>
  );
}
